import { PostHogEvent } from '@/lib/analytics/posthog-events';
import { tryManagedAssetDownload } from '@/lib/managed-asset-download';
import { isAppleTouchDevice, saveBlob } from '@/lib/save-blob';
import posthog from 'posthog-js';

export type AssetDownloadTrackingInfo = {
  assetId: string;
  source: string;
  fileType?: string | null;
  [key: string]: unknown;
};

function isSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|Chromium/.test(ua);
}

export function isSameOrigin(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const urlObj = new URL(url, window.location.href);
    return urlObj.origin === window.location.origin;
  } catch {
    return false;
  }
}

export function getProxiedUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url === 'internal' || url === 'logo') return '';
  if (url.startsWith('http') && !isSameOrigin(url)) {
    return `/api/proxy-media?url=${encodeURIComponent(url)}`;
  }
  return url;
}

export async function downloadOrShareAsset(
  url: string,
  filename?: string,
  assetType?: 'image' | 'video' | '3d',
  trackingInfo?: AssetDownloadTrackingInfo
): Promise<void> {
  if (typeof window === 'undefined') return;

  if (trackingInfo) {
    captureAssetDownload(trackingInfo, assetType);
  }

  const urlPath = new URL(url, window.location.href).pathname;
  const urlFilename = urlPath.split('/').pop();
  let finalFilename = filename || urlFilename || 'asset';
  finalFilename = finalFilename.split('?')[0]?.split('#')[0] || finalFilename;

  if (!finalFilename.includes('.')) {
    if (assetType === 'video') {
      finalFilename += '.mp4';
    } else if (assetType === 'image') {
      finalFilename += '.png';
    } else if (assetType === '3d') {
      finalFilename += '.glb';
    } else {
      finalFilename += '.png';
    }
  }

  try {
    if (
      await tryManagedAssetDownload({
        assetType,
        filename: finalFilename,
        trackingInfo,
      })
    ) {
      return;
    }
  } catch (error) {
    console.warn(
      '[Download] Managed download failed, falling back to browser download:',
      error
    );
  }

  // iPhone and iPad open a same-origin PDF link instead of saving it.
  if (isSafari() && isSameOrigin(url) && !isAppleTouchDevice(navigator)) {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFilename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    } catch (error) {
      console.warn(
        '[Download] Direct link download failed, trying blob approach:',
        error
      );
    }
  }

  try {
    const fetchUrl = isSameOrigin(url)
      ? url
      : `/api/proxy-media?url=${encodeURIComponent(url)}`;
    const response = await fetch(fetchUrl, {
      mode: 'cors',
      credentials: 'omit',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    // Untyped storage objects still reach the share sheet as their media type.
    const fallbackType =
      assetType === 'video'
        ? 'video/mp4'
        : assetType === 'image'
          ? 'image/png'
          : '';
    const typed =
      (!blob.type || blob.type === 'application/octet-stream') && fallbackType
        ? new Blob([blob], { type: fallbackType })
        : blob;
    await saveBlob(typed, finalFilename, { sourceUrl: url });
  } catch (error) {
    console.error('[Download] Blob download failed:', error);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function captureAssetDownload(
  trackingInfo: AssetDownloadTrackingInfo,
  assetType?: 'image' | 'video' | '3d'
): void {
  const eventData = {
    ...trackingInfo,
    assetType: assetType || 'image',
    fileType: trackingInfo.fileType ?? assetType ?? 'image',
  };
  posthog.capture(PostHogEvent.ASSET_DOWNLOADED, eventData);
}
